import React, { useState } from 'react';
import { CheckCircle2Icon, PhoneIcon } from 'lucide-react';
import { serviceCategories } from '../data/serviceCategories';
import { business } from '../data/site';
import { FormPolicyLinks } from './FormPolicyLinks';
export const ANY_SERVICE = 'not-sure';
export function categoryOptionValue(categorySlug: string) {
  return `${categorySlug}/any`;
}
export function serviceOptionValue(categorySlug: string, serviceSlug: string) {
  return `${categorySlug}/${serviceSlug}`;
}
type HeroLeadFormProps = {
  defaultService?: string;
  sourcePage: string;
};
const fieldClass = 'w-full rounded-md border border-white/15 bg-ink px-3 py-2.5 text-[14px] leading-tight text-white outline-none transition-colors duration-150 ease-out placeholder:text-chalk/35 focus:border-brand-light';
const labelClass = 'block text-[11px] font-semibold uppercase tracking-[0.1em] text-chalk/55';
export function HeroLeadForm({
  defaultService = ANY_SERVICE,
  sourcePage
}: HeroLeadFormProps) {
  const [submitted, setSubmitted] = useState(false);
  if (submitted) {
    return <div className="rounded-lg border border-white/10 bg-charcoal p-5" data-id="element-217" data-mp="rbr7erxf2n2j">
        <CheckCircle2Icon className="h-7 w-7 text-brand-light" aria-hidden="true" data-id="element-218" data-mp="rd28gjzh9gpy" />
        <h2 className="mt-3 font-display text-[18px] font-bold tracking-tight text-white" data-id="element-219" data-mp="dece3c41lv5a">
          Preview only
        </h2>
        <p className="mt-1.5 text-[14px] leading-relaxed text-chalk/70" data-id="element-220" data-mp="160x1k1hedn3k">
          Your request has not been sent. Please call the shop at
          {' '}{business.phone} for help.
        </p>
        <a href={business.phoneHref} className="mt-4 inline-flex items-center gap-2.5 whitespace-nowrap rounded-md bg-brand px-4 py-2.5 font-display text-[15px] font-bold text-white transition-colors duration-150 ease-out hover:bg-brand-light" data-id="element-221" data-mp="wb3sp88n3m30">
          <PhoneIcon className="h-4 w-4 shrink-0" aria-hidden="true" data-id="element-222" data-mp="1oxgaapoah8zl" />
          Call {business.phone}
        </a>
      </div>;
  }
  return <form onSubmit={e => {
    e.preventDefault();
    setSubmitted(true);
  }} data-source-page={sourcePage} className="rounded-lg border border-white/10 bg-charcoal p-4 lg:p-5" data-id="element-223" data-mp="23mhxzmew6srh">
      <h2 className="font-display text-[17px] font-bold tracking-tight text-white" data-id="element-224" data-mp="1tmqzl9n7ntm4">
        Request a call back
      </h2>
      <p className="mt-1 text-[12.5px] leading-snug text-chalk/60" data-id="element-225" data-mp="1oopkezswoefx">
        Calling {business.phone} is the fastest way to reach the shop.
      </p>

      <div className="mt-3.5 grid gap-2.5 sm:grid-cols-2" data-id="element-226" data-mp="1fw40vhhga3q1">
        <div className="sm:col-span-2" data-id="element-227" data-mp="1fuknbyn6i6ry">
          <label className={labelClass} htmlFor={`${sourcePage}-name`} data-id="element-228" data-mp="1awrfl16894pk">
            Full name
          </label>
          <input id={`${sourcePage}-name`} name="name" type="text" required autoComplete="name" className={`mt-1 ${fieldClass}`} data-id="element-229" data-mp="15ziwmyd98wn9" />
        </div>
        <div data-id="element-230" data-mp="wyl1l5ksnzgt">
          <label className={labelClass} htmlFor={`${sourcePage}-phone`} data-id="element-231" data-mp="wzw2mxmuut48">
            Phone number
          </label>
          <input id={`${sourcePage}-phone`} name="phone" type="tel" required autoComplete="tel" className={`mt-1 ${fieldClass}`} data-id="element-232" data-mp="3cmq9q6hdfrxe" />
        </div>
        <div data-id="element-233" data-mp="3ml61mbo8357y">
          <label className={labelClass} htmlFor={`${sourcePage}-email`} data-id="element-234" data-mp="3mjuz0f6ik3if">
            Email address
          </label>
          <input id={`${sourcePage}-email`} name="email" type="email" required autoComplete="email" className={`mt-1 ${fieldClass}`} data-id="element-235" data-mp="2oyqlpebg2fmi" />
        </div>
        <div className="sm:col-span-2" data-id="element-236" data-mp="2ytife71woic0">
          <label className={labelClass} htmlFor={`${sourcePage}-service`} data-id="element-237" data-mp="33mbk4uxdjqo3">
            Service interest
          </label>
          <select id={`${sourcePage}-service`} name="service" required defaultValue={defaultService} className={`mt-1 ${fieldClass}`} data-id="element-238" data-mp="2664sl1rx935m">
            <option value={ANY_SERVICE} data-id="element-239" data-mp="gq2a3u3gcopp">Not sure - please diagnose</option>
            {serviceCategories.map(category => <optgroup key={category.slug} label={category.name} data-id="element-240" data-mp="cm47h9ex4vrw">
                <option value={categoryOptionValue(category.slug)} data-id="element-241" data-mp="qgwt8plej86x">
                  {category.name} - not sure which service
                </option>
                {category.services.map(service => <option key={service.slug} value={serviceOptionValue(category.slug, service.slug)} data-id="element-242" data-mp="10bot602gi74v">
                    {service.name}
                  </option>)}
              </optgroup>)}
          </select>
        </div>
        <div className="sm:col-span-2" data-id="element-243" data-mp="14d0i03diaoc0">
          <label className={labelClass} htmlFor={`${sourcePage}-vehicle`} data-id="element-244" data-mp="3b51fvjn9jn4x">
            Year / make / model{' '}
            <span className="font-normal normal-case tracking-normal text-chalk/35" data-id="element-245" data-mp="367jecebksa6y">
              (optional)
            </span>
          </label>
          <input id={`${sourcePage}-vehicle`} name="vehicle" type="text" className={`mt-1 ${fieldClass}`} data-id="element-246" data-mp="1lqihyerhg5wl" />
        </div>
        <div className="sm:col-span-2" data-id="element-247" data-mp="1cvlzswpsbiwl">
          <label className={labelClass} htmlFor={`${sourcePage}-message`} data-id="element-248" data-mp="1gxh2o5nd64ch">
            What is the vehicle doing?{' '}
            <span className="font-normal normal-case tracking-normal text-chalk/35" data-id="element-249" data-mp="12xoeoi382287">
              (optional)
            </span>
          </label>
          <textarea id={`${sourcePage}-message`} name="message" rows={2} className={`mt-1 resize-y ${fieldClass}`} data-id="element-250" data-mp="17yc1ssobxyx5" />
        </div>
      </div>

      <button type="submit" className="mt-3.5 w-full rounded-md border border-white/25 px-5 py-2.5 text-[14px] font-semibold text-white transition-colors duration-150 ease-out hover:border-white/60" data-id="element-251" data-mp="y0xo30tsjo8j">
        Request a Call Back
      </button>
      <FormPolicyLinks data-id="element-252" data-mp="55hnxjjrhlhs" />
    </form>;
}